import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isSFWContent, getInappropriateWordsFound } from '../utils/contentFilter';
import axios from 'axios';
import config from '../config';

const Dashboard: React.FC = () => {
  const [roomKey, setRoomKey] = useState('');
  const [roomName, setRoomName] = useState('');
  const [gridSize, setGridSize] = useState(5);
  const [lives, setLives] = useState(3);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [roomNameWarning, setRoomNameWarning] = useState('');
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleRoomNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRoomName = e.target.value;
    setRoomName(newRoomName);
    
    // Real-time SFW validation
    if (newRoomName && !isSFWContent(newRoomName)) {
      const inappropriateWords = getInappropriateWordsFound(newRoomName);
      setRoomNameWarning(`⚠️ Inappropriate content detected: ${inappropriateWords.join(', ')}`);
    } else {
      setRoomNameWarning('');
    }
  };

  const handleCreateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Check if room name is SFW
    if (!isSFWContent(roomName)) {
      const inappropriateWords = getInappropriateWordsFound(roomName);
      setError(`Room name contains inappropriate content. Please choose a different name. Found: ${inappropriateWords.join(', ')}`);
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('room_name', roomName);
      formData.append('grid_size', gridSize.toString());
      formData.append('lives', lives.toString());

      const response = await axios.post(`${config.API_BASE_URL}/create_room`, formData);
      const { room_key } = response.data;
      
      setSuccess(`Room created! Room key: ${room_key}`);
      navigate(`/room/${room_key}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create room');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('room_key', roomKey);

      await axios.post(`${config.API_BASE_URL}/join_room`, formData);
      navigate(`/room/${roomKey}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to join room');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1>Welcome, {user?.username}!</h1>
          <button onClick={logout} className="btn btn-danger">Logout</button>
        </div>
        
        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Create Room */}
          <div className="card">
            <h2>Create Room</h2>
            <form onSubmit={handleCreateRoom}>
              <div className="form-group">
                <label htmlFor="roomName">Room Name:</label>
                <input
                  type="text"
                  id="roomName"
                  value={roomName}
                  onChange={handleRoomNameChange}
                  required
                  style={{ 
                    borderColor: roomNameWarning ? '#ff4444' : undefined,
                    backgroundColor: roomNameWarning ? '#ffe6e6' : undefined
                  }}
                />
                {roomNameWarning && (
                  <div style={{ 
                    color: '#ff4444', 
                    fontSize: '12px', 
                    marginTop: '5px',
                    fontWeight: 'bold'
                  }}>
                    {roomNameWarning}
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="gridSize">Grid Size:</label>
                <select
                  id="gridSize"
                  value={gridSize}
                  onChange={(e) => setGridSize(parseInt(e.target.value))}
                  style={{ width: '100%', padding: '12px', border: 'none', borderRadius: '8px', background: 'rgba(255, 255, 255, 0.9)', color: '#333', fontSize: '16px' }}
                >
                  <option value={3}>3x3</option>
                  <option value={4}>4x4</option>
                  <option value={5}>5x5</option>
                  <option value={6}>6x6</option>
                  <option value={7}>7x7</option>
                  <option value={8}>8x8</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="lives">Lives:</label>
                <select
                  id="lives"
                  value={lives}
                  onChange={(e) => setLives(parseInt(e.target.value))}
                  style={{ width: '100%', padding: '12px', border: 'none', borderRadius: '8px', background: 'rgba(255, 255, 255, 0.9)', color: '#333', fontSize: '16px' }}
                >
                  <option value={1}>1 Life</option>
                  <option value={2}>2 Lives</option>
                  <option value={3}>3 Lives</option>
                  <option value={4}>4 Lives</option>
                  <option value={5}>5 Lives</option>
                </select>
              </div>
              
              <button 
                type="submit" 
                className="btn btn-success" 
                disabled={loading || !!roomNameWarning}
                style={{
                  opacity: roomNameWarning ? 0.6 : undefined,
                  cursor: roomNameWarning ? 'not-allowed' : undefined
                }}
              >
                {loading ? 'Creating...' : 'Create Room'}
              </button>
            </form>
          </div>
          
          {/* Join Room */}
          <div className="card">
            <h2>Join Room</h2>
            <form onSubmit={handleJoinRoom}>
              <div className="form-group">
                <label htmlFor="roomKey">Room Key:</label>
                <input
                  type="text"
                  id="roomKey"
                  value={roomKey}
                  onChange={(e) => setRoomKey(e.target.value.toUpperCase())}
                  placeholder="Enter room key"
                  required
                />
              </div>
              
              <button type="submit" className="btn btn-secondary" disabled={loading}>
                {loading ? 'Joining...' : 'Join Room'}
              </button>
            </form>
          </div>
        </div>
        
        <div className="card" style={{ marginTop: '20px' }}>
          <h2>How to Play</h2>
          <div style={{ textAlign: 'left', lineHeight: '1.6' }}>
            <p><strong>1. Create or Join a Room:</strong> Host creates a room with grid size and lives, others join with the room key.</p>
            <p><strong>2. Wait for Game Start:</strong> Host starts the game when all players are ready.</p>
            <p><strong>3. Get Your Position:</strong> Each player gets a secret coordinate on the grid.</p>
            <p><strong>4. Make Guesses:</strong> Players guess coordinates of other players.</p>
            <p><strong>5. Vote on Guesses:</strong> All players vote if the guess is correct or not.</p>
            <p><strong>6. Win or Lose Lives:</strong> Correct guesses continue the game, wrong guesses lose lives.</p>
            <p><strong>7. Game Over:</strong> When all lives are lost, the game ends.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
